/**
 * Página de Actualidad/Noticias - Estilo Awwwards Interior
 * Diseño elegante y funcional con animaciones sutiles
 * Siguiendo el mismo estilo que la página de Nosotros
 */

import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImmersiveLayout from '../components/layout/ImmersiveLayout';
import { AnimatedSection } from '../components/animations';
import { getAllNews } from '../data/newsData';

gsap.registerPlugin(ScrollTrigger);

/**
 * Página principal de noticias con estilo Awwwards interior
 * @returns {JSX.Element} Página de noticias inmersiva
 */
const NewsPage = () => {
  const news = getAllNews();
  const heroRef = useRef(null);
  const cardsRef = useRef(null);

  // Animación del hero al cargar
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const tl = gsap.timeline();
    
    // Animación de entrada del hero
    tl.fromTo(hero.querySelector('.hero-title'),
      { y: 40, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }
    )
    .fromTo(hero.querySelector('.hero-subtitle'),
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' },
      '-=0.4'
    );

    return () => tl.kill();
  }, []);

  // Animación de las cards de noticias
  useEffect(() => {
    const cards = cardsRef.current?.querySelectorAll('.news-card');
    if (!cards) return;

    cards.forEach((card, index) => {
      gsap.fromTo(card,
        { y: 60, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          delay: index * 0.1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: card,
            start: 'top 85%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <ImmersiveLayout>
      {/* 
        =============================================
        1. HERO SUPERIOR - Estilo página interior
        =============================================
      */}
      <section 
        ref={heroRef}
        className="relative min-h-[60vh] flex items-center overflow-hidden"
      >
        {/* Fondo oscuro base */}
        <div className="absolute inset-0 bg-intercasa-dark" />
        
        {/* Imagen de fondo con opacidad */}
        <div 
          className="absolute inset-0 opacity-70"
          style={{
            backgroundImage: 'url(/images/noticias/fabrica-noticia-1.webp)',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
        />
        
        {/* Gradiente para mejor legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-r from-intercasa-dark/90 via-intercasa-dark/70 to-intercasa-dark/90" />
        
        {/* Elementos decorativos sutiles */}
        <div className="absolute top-1/2 right-0 w-[500px] h-[500px] bg-intercasa-red/5 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-intercasa-red/5 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>
        
        <div className="container mx-auto px-4 md:px-8 relative z-10 py-20">
          <div className="max-w-4xl mx-auto text-center">
            {/* Etiqueta superior */}
            <AnimatedSection animation="fadeUp" delay={0}>
              <span className="inline-block font-lato text-intercasa-yellow text-sm uppercase tracking-[0.2em] mb-6">
                Últimas novedades
              </span>
            </AnimatedSection>

            {/* Título principal */}
            <h1 className="hero-title font-baskerville font-bold text-white text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[1.1] mb-6">
              Actualidad{' '}
              <span className="text-intercasa-yellow">Intercasa</span>
            </h1>
            
            {/* Subtítulo */}
            <p className="hero-subtitle font-lato text-white/90 text-lg md:text-xl lg:text-2xl leading-relaxed max-w-3xl mx-auto text-center">
              Novedades, mejoras en fábrica, acciones sociales y noticias de nuestra empresa.
            </p>
          </div>
        </div>
      </section>

      {/* 
        =============================================
        2. INTRODUCCIÓN
        =============================================
      */}
      <section className="bg-white py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection animation="fadeUp">
              <p className="font-lato text-lg text-intercasa-dark/70 leading-relaxed">
                Descubre las acciones que realizamos en Intercasa: mejoras en nuestras instalaciones, iniciativas de sostenibilidad, actividades internas, colaboraciones y todas las novedades relacionadas con nuestra empresa.
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* 
        =============================================
        3. GRID DE NOTICIAS
        =============================================
      */}
      <section className="bg-intercasa-bone py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-8">
          {/* Header de sección */}
          <AnimatedSection animation="fadeUp" className="mb-16">
            <span className="font-lato text-intercasa-red text-sm uppercase tracking-widest mb-4 block">
              Blog
            </span>
            <h2 className="font-baskerville font-bold text-intercasa-dark text-3xl md:text-4xl lg:text-5xl">
              Noticias recientes
            </h2>
          </AnimatedSection>

          {/* Grid de noticias */}
          <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {news.map((newsItem, index) => (
              <Link
                key={newsItem.id}
                to={`/noticias/${newsItem.slug}`}
                className="news-card group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500"
              >
                {/* Imagen */}
                <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
                  <div className="absolute inset-0 transition-transform duration-700 group-hover:scale-105">
                    {newsItem.imageUrl ? (
                      <img 
                        src={newsItem.imageUrl} 
                        alt={newsItem.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-intercasa-red/10 to-intercasa-red/5 flex items-center justify-center">
                        <svg className="w-16 h-16 text-intercasa-red/30" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {/* Badge de fecha */}
                  <div className="absolute top-4 left-4 z-10">
                    <span className="inline-block px-3 py-1 bg-white/90 backdrop-blur-sm text-intercasa-dark text-xs font-bold rounded-full">
                      {newsItem.date}
                    </span>
                  </div>
                </div>

                {/* Contenido */}
                <div className="p-6">
                  {/* Categoría */}
                  {newsItem.category && (
                    <span className="inline-block text-xs text-intercasa-red font-bold uppercase tracking-wider mb-3">
                      {newsItem.category}
                    </span>
                  )}
                  
                  <h3 className="font-baskerville font-bold text-xl text-intercasa-dark mb-3 group-hover:text-intercasa-red transition-colors duration-300 break-words hyphens-none">
                    {newsItem.title}
                  </h3>
                  
                  {newsItem.summary && (
                    <p className="text-intercasa-gray text-sm mb-4">
                      {newsItem.summary.split('.')[0]}.
                    </p>
                  )}

                  {/* Link */}
                  <span className="inline-flex items-center text-intercasa-red font-semibold text-sm group-hover:gap-3 gap-2 transition-all duration-300">
                    Leer más
                    <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </span>
                </div>

                {/* Línea inferior animada */}
                <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-red transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 
        =============================================
        4. CTA FINAL - COMPROMISO CON CANARIAS
        =============================================
      */}
      <section className="bg-intercasa-dark py-24 md:py-32 relative overflow-hidden">
        {/* Elementos decorativos */}
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-intercasa-red/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-intercasa-red/10 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection animation="fadeUp">
              <span className="font-lato text-intercasa-red text-sm uppercase tracking-widest mb-6 block">
                Más de 75 años
              </span>
              
              <h2 className="font-baskerville font-bold text-white text-3xl md:text-4xl lg:text-5xl mb-8 leading-tight">
                Compromiso con<br />
                <span className="text-intercasa-yellow">Canarias</span>
              </h2>
              
              <p className="font-lato text-lg text-white/60 leading-relaxed mb-12">
                En Intercasa seguimos trabajando cada día para mejorar, innovar y aportar valor a Canarias. 
                Nuestra empresa familiar continúa creciendo con los mismos valores que nos han acompañado durante más de 75 años.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/nosotros"
                  className="group inline-flex items-center justify-center bg-white text-intercasa-dark font-semibold px-8 py-4 rounded-full hover:bg-intercasa-yellow transition-all duration-300"
                >
                  Conoce nuestra historia
                  <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
                
                <Link 
                  to="/productos"
                  className="group inline-flex items-center justify-center bg-transparent border-2 border-white/30 text-white font-semibold px-8 py-4 rounded-full hover:border-white hover:bg-white/10 transition-all duration-300"
                >
                  Ver nuestros productos
                  <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </ImmersiveLayout>
  );
};

export default NewsPage;